import React, { useState, useEffect, useRef } from "react";
import { getCurrencySymbol } from '../utils/currency';
import {
  FaTimes,
  FaComment,
  FaShareAlt,
} from "react-icons/fa";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { writeUserDocument } from "@services/firebaseUser";
import { getFirebase } from "@services/firebase";
import { fetchCombinations } from "@services/firebaseGlobal";
import { useNavigate } from "react-router-dom";
import { useToast } from "../contexts/ToastContext";
import "../index.css";

let db;

const getDb = () => {
  if (!db) {
    const { db: firebaseDb } = getFirebase();
    db = firebaseDb;
  }
  return db;
};

// Suffix ↔ label mappings for weight-based pricing keys (e.g. N10 → 1kg)
const SUFFIX_TO_LABEL = {
  10: "1",
  15: "1.5",
  20: "2",
  25: "2.5",
  30: "3",
  35: "3.5",
  40: "4",
  45: "4.5",
  50: "5",
  55: "5.5",
  60: "6",
};
const LABEL_TO_SUFFIX = {
  "1": "10",
  "1.5": "15",
  "2": "20",
  "2.5": "25",
  "3": "30",
  "3.5": "35",
  "4": "40",
  "4.5": "45",
  "5": "50",
  "5.5": "55",
  "6": "60",
};

const styles = `
  .profile-card-large-container {
    position: relative;
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 8px;
    width: 85vw;
    max-width: 400px;
    height: auto;
    margin: 0 auto;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    box-sizing: border-box;
    overflow: hidden;
  }

  .profile-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 15px;
    border-bottom: 1px solid #f0f0f0;
    position: relative;
    min-height: 50px;
  }

  .profile-card-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 600;
    color: #333;
    flex: 1;
    justify-content: center;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 10px;
  }

  .profile-card-close-btn {
    position: absolute;
    top: 0;
    right: 0;
    background: transparent;
    border: none;
    font-size: 16px;
    cursor: pointer;
    color: #666;
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
    z-index: 1;
  }

  .profile-card-close-btn:hover {
    color: #333;
  }

  .profile-card-content {
    padding: 15px;
    padding-bottom: 25px; /* Added more bottom padding */
    overflow-y: auto;
  }

  .action-btn {
    background: none !important;
    border: none !important;
    color: white !important;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    text-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
  }

  .action-btn:hover:not(:disabled) {
    color: #fa4848 !important;
    transform: scale(1.1);
  }

  .action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed !important;
  }

  .logo-chat-container {
    display: flex;
    align-items: center;
    gap: 18px;
  }

  .logo-chat-container button {
    background: none;
    border: none;
    cursor: pointer;
    padding: '8px';
    border-radius: '50%';
    transition: 'background 0.2s';
    display: 'flex';
    align-items: 'center';
    justify-content: 'center';
  }

  .logo-chat-container button:hover {
    background: #f0f0f0;
  }

  .logo-chat-container button svg {
    color: #666;
    font-size: 22px;
  }

  html[data-theme="dark"] .logo-chat-container button svg {
    color: #ffffff;
  }

  html[data-theme="dark"] .logo-chat-container button:hover {
    background: rgba(255,255,255,0.12);
  }

  @media (max-width: 450px) {
    .profile-card-large-container {
      width: 95vw;
      max-height: 90vh;
    }

    .profile-card-title {
      font-size: 16px;
    }

    .action-btn {
      width: 32px;
      height: 32px;
    }

    .profile-card-header {
      padding: 10px 12px;
      min-height: 45px;
    }
  }
`;

const HomeProfileCardLarge = ({
  theme,
  flavour,
  filling,
  cover,
  sponge,
  cost,
  image,
  cshape,
  onClose,
  onShare = () => {},
  isInEnquiry = false,
  user,
  userRole,
  profileId,
}) => {
  const is3TierCake = Array.isArray(cshape)
    ? cshape.some((s) => typeof s === "string" && s.toLowerCase() === "3tier")
    : typeof cshape === "string" && cshape.toLowerCase() === "3tier";
  const isTierCake = Array.isArray(cshape)
    ? cshape.some(
        (s) => typeof s === "string" && s.toLowerCase().includes("tier")
      )
    : typeof cshape === "string" && cshape.toLowerCase().includes("tier");

  const showToast = useToast();
  const [selectedQuantity, setSelectedQuantity] = useState("");
  const [starRating, setStarRating] = useState(0);
  const [isInCart, setIsInCart] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartLoading, setCartLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showOngoingModal, setShowOngoingModal] = useState(false);
  const [cakeChargeMap, setCakeChargeMap] = useState({});
  const [personServedMap, setPersonServedMap] = useState({});
  const [cakePriceSegment, setCakePriceSegment] = useState("N");
  const [coverPriceSegment, setCoverPriceSegment] = useState("F");
  const [coverFrosting, setCoverFrosting] = useState(null);

  const navigate = useNavigate();
  const cardRef = useRef(null);

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const isBuyer = userRole === "Buyer";
  const isAdmin = userRole === "Admin";
  const isSuper = userRole === "Super";
  const isUser = userRole === "User";
  const showCartOptions = isBuyer;
  const showWeightButtons = isBuyer || isAdmin || isSuper;

  // Dynamic quantity options: 3T keys for 3-tier cakes, T keys for tier cakes, N keys for normal
  const baseSegment = is3TierCake ? "3T" : isTierCake ? "T" : "N";
  const quantityOptions = (() => {
    const suffixes = [
      ...new Set(
        Object.keys(cakeChargeMap)
          .filter((k) => new RegExp(`^${baseSegment}\\d+$`).test(k))
          .map((k) => k.slice(baseSegment.length))
      ),
    ].sort((a, b) => parseInt(a) - parseInt(b));
    return suffixes.map((s) => SUFFIX_TO_LABEL[s]).filter(Boolean);
  })();

  // Pricing logic:
  //   ccost (from profile) = N_base + S_base (if S) + B_base (if B) + design_premium
  //   design_premium = ccost - N_base - S_base - B_base
  //   price(n) = N_base + N_delta(n) + S_flat(n) + B_flat(n) + design_premium
  //            = ccost - S_base - B_base + N_delta(n) + S_flat(n) + B_flat(n)
  //   at base weight this always equals ccost. If ccost is empty → all prices empty.
  const baseSuffix = (() => {
    const nums = Object.keys(cakeChargeMap)
      .filter((k) => new RegExp(`^${baseSegment}\\d+$`).test(k))
      .map((k) => parseInt(k.slice(baseSegment.length)))
      .filter((n) => !isNaN(n));
    return nums.length ? String(Math.min(...nums)) : isTierCake ? "15" : "10";
  })();
  const priceSuffix = LABEL_TO_SUFFIX[selectedQuantity] || "";
  const ccostBase = parseFloat(cost || 0);
  const adjustedCost = (() => {
    if (!priceSuffix || !ccostBase) return "";
    const nBase = parseFloat(cakeChargeMap[baseSegment + baseSuffix] || 0);
    const sBase =
      cakePriceSegment === "S"
        ? parseFloat(cakeChargeMap["S" + baseSuffix] || 0)
        : 0;
    const bBase =
      !isTierCake && coverPriceSegment === "B"
        ? parseFloat(cakeChargeMap["B" + baseSuffix] || 0)
        : 0;
    const nDelta =
      priceSuffix !== baseSuffix
        ? parseFloat(cakeChargeMap[baseSegment + priceSuffix] || 0)
        : 0;
    const sFlat =
      cakePriceSegment === "S"
        ? parseFloat(cakeChargeMap["S" + priceSuffix] || 0)
        : 0;
    const bFlat =
      !isTierCake && coverPriceSegment === "B"
        ? parseFloat(cakeChargeMap["B" + priceSuffix] || 0)
        : 0;
    return (ccostBase - sBase - bBase + nDelta + sFlat + bFlat).toFixed(2);
  })();

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Load cCakeCharge pricing data, and look up price segments for the combo/cover
  useEffect(() => {
    const loadPricingData = async () => {
      try {
        const [charges, combinations, coverCombis] = await Promise.all([
          fetchCombinations("cCakeCharge"),
          fetchCombinations("oCombination"),
          fetchCombinations("oCoverCombi"),
        ]);

        // Build variant→cost map and variant→personServed map
        const chargeMap = {};
        const psMap = {};
        charges.forEach(({ variant, cost: chargeVal, personServed }) => {
          if (variant) {
            chargeMap[variant] = chargeVal;
            if (personServed) psMap[variant] = personServed;
          }
        });
        setCakeChargeMap(chargeMap);
        setPersonServedMap(psMap);

        // Determine cake price segment (N or S) from flavour+filling match
        const flavourStr = flavour || "";
        const fillingStr = filling || "";
        const comboMatch =
          combinations.find(
            (c) => c.flavour === flavourStr && c.filling === fillingStr
          ) || combinations.find((c) => c.flavour === flavourStr);
        if (
          comboMatch &&
          (comboMatch.priceSegment === "N" || comboMatch.priceSegment === "S")
        ) {
          setCakePriceSegment(comboMatch.priceSegment);
        }

        // Determine cover price segment (F = no surcharge, B = surcharge) from cover match
        const coverStr = cover || "";
        const coverMatch = coverCombis.find((c) => c.cover === coverStr);
        if (
          coverMatch &&
          (coverMatch.priceSegment === "F" || coverMatch.priceSegment === "B")
        ) {
          setCoverPriceSegment(coverMatch.priceSegment);
        }
        if (coverMatch?.frosting) {
          setCoverFrosting(coverMatch.frosting);
        }

        // Default selected quantity to first available option (3T/T/N keys)
        const keyPfx = is3TierCake ? "3T" : isTierCake ? "T" : "N";
        const firstSuffix = [
          ...new Set(
            Object.keys(chargeMap)
              .filter((k) => new RegExp(`^${keyPfx}\\d+$`).test(k))
              .map((k) => k.slice(keyPfx.length))
          ),
        ].sort((a, b) => parseInt(a) - parseInt(b))[0];
        if (firstSuffix && SUFFIX_TO_LABEL[firstSuffix]) {
          setSelectedQuantity(SUFFIX_TO_LABEL[firstSuffix]);
        }
      } catch (err) {
        console.error("Error loading pricing data:", err);
      }
    };
    loadPricingData();
  }, [flavour, filling, cover, isTierCake]);

  // Listen to enquiries (cart status + popularity rating)
  useEffect(() => {
    if (!user || !db || !profileId) {
      setIsInCart(false);
      setLoading(false);
      return;
    }

    const q = query(collection(db, "enquiry"), where("userId", "==", user.uid));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const updatedEnquiries = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setEnquiries(updatedEnquiries);

        const present = updatedEnquiries.some((e) => e.globalId === profileId);
        setIsInCart(present);

        // Popularity stars
        if (theme && updatedEnquiries.length > 0) {
          const themeCount = updatedEnquiries.filter(
            (e) => e.theme === theme
          ).length;
          const percentage = (themeCount / updatedEnquiries.length) * 100;
          if (percentage > 30) setStarRating(5);
          else if (percentage > 20) setStarRating(4);
          else if (percentage > 10) setStarRating(3);
          else setStarRating(0);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Enquiry listener error:", err);
        setError("Failed to load cart status");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, profileId, theme]);

  const handleChatClick = (e) => {
    e.stopPropagation();
    if (!user) return;

    const profileData = {
      image,
      theme: theme || "",
      flavour: flavour || "",
      filling: filling || "",
      cover: cover || "",
      sponge: sponge || "",
      cost: cost || "",
      cshape: cshape || [],
      profileId: profileId || "",
      timestamp: new Date().toISOString(),
    };
    sessionStorage.setItem("chatProfileData", JSON.stringify(profileData));

    if (isAdmin || isSuper) {
      navigate("/admin-chat");
    } else {
      navigate("/user-chat");
    }
    onClose();
  };

  const addToCart = async (e) => {
    e.stopPropagation();
    if (!user || isInCart || isInEnquiry) return;
    try {
      const BLOCKING_STATES = [
        "Request Review",
        "Review Pending",
        "Review In-Progress",
        "Review Feedback",
      ];
      const firestore = getDb();
      const userQ = where("userId", "==", user.uid);
      const [enqSnap, ordSnap] = await Promise.all([
        getDocs(query(collection(firestore, "enquiry"), userQ)),
        getDocs(query(collection(firestore, "order"), userQ)),
      ]);
      const hasOngoing = [...enqSnap.docs, ...ordSnap.docs].some((d) => {
        const s = d.data().reqState;
        return s && BLOCKING_STATES.includes(s);
      });
      if (hasOngoing) {
        setShowOngoingModal(true);
        return;
      }
      setCartLoading(true);
      const enquiryData = {
        cover: cover || null,
        frosting: coverFrosting || null,
        eggless: false,
        glutenFree: false,
        quantity: selectedQuantity || null,
        ccost: null,
        costest: adjustedCost
          ? String(adjustedCost).replace(/^€|^EUR/i, "")
          : null,
        images: image ? [image] : [],
        userId: user.uid,
        addedAt: serverTimestamp(),
        globalId: profileId,
        theme: theme || null,
        tiers: [
          {
            tier: 1,
            cflavour: flavour || null,
            cfilling: filling || null,
            sponge: sponge || null,
          },
        ],
        tierCount: 1,
        reqState: "New Request",
        isFromHome: true,
      };

      await writeUserDocument("enquiry", user.uid, enquiryData);
      showToast("Added to enquiry!");
      onClose();
    } catch (err) {
      console.error("addToCart error:", err);
      showToast(`Failed to add to cart: ${err.message}`);
    } finally {
      setCartLoading(false);
    }
  };

  const formatDescription = () => {
    const text = `${flavour?.toLowerCase() || "unknown"} flavour with ${
      filling?.toLowerCase() || "none"
    } filling, covered by ${cover?.toLowerCase() || "none"}.`;
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "10px",
      }}
    >
      <div ref={cardRef} className="profile-card-large-container">
        <style>{styles}</style>

        {/* Close button always at top-right corner */}
        <button className="profile-card-close-btn" onClick={onClose}>
          <FaTimes />
        </button>

        {/* Header - Only show if there's a theme */}
        {theme && (
          <div className="profile-card-header">
            <div className="profile-card-title">{theme}</div>
          </div>
        )}

        <div className="profile-card-content">
          {/* Image Container - Centered properly */}
          <div
            style={{
              position: "relative",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginBottom: "15px",
            }}
          >
            <div
              style={{
                position: "center",
                width: "90%",
                aspectRatio: "4 / 3",
                overflow: "hidden",
                backgroundColor: "#f0f0f0",
                borderRadius: "8px",
                marginRight: "25px",
              }}
            >
              {image ? (
                <img
                  src={image}
                  alt={flavour || "Cake"}
                  style={{
                    width: "100%",
                    height: "100%",
                    minWidth: "100%",
                    minHeight: "100%",
                    objectFit: "fill",
                    objectPosition: "center",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  No Image
                </div>
              )}

              {/* NO ACTION BUTTONS ON IMAGE - COMPLETELY REMOVED */}
            </div>
          </div>

          {/* Custom button moved to where logo was, with Chat and Share buttons */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "12px 0 15px 0",
              width: "100%",
            }}
          >
            {/* Left side: Chat, Share */}
            <div
              className="logo-chat-container"
              style={{
                margin: 0,
                paddingLeft: "5px",
                justifyContent: "flex-start",
              }}
            >
              {/* Chat button */}
              <button className="lcc-btn" onClick={handleChatClick}>
                <FaComment />
              </button>

              {/* Share button */}
              <button
                className="lcc-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onShare();
                }}
              >
                <FaShareAlt />
              </button>
            </div>

            {/* Right side: Popularity - Extreme right */}
            {starRating >= 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  marginRight: "30px",
                }}
              >
                <p
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    margin: "0 0 2px 0",
                    whiteSpace: "nowrap",
                  }}
                >
                  Popularity
                </p>
                <div>
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      style={{
                        color: i < starRating ? "#fa4848" : "#ccc",
                        fontSize: "14px",
                      }}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ margin: "0px 10px 0px 0px" }}>
            <p
              className="hpcl-description"
              style={{
                position: "center",
                fontSize: "16px",
                lineHeight: "1.5",
                background: "none",
                borderRadius: "6px",
              }}
            >
              {formatDescription()}
            </p>
          </div>

          {/* KG selector – smaller buttons */}
          {showWeightButtons && (
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                gap: "6px",
                justifyContent: "left",
                margin: "2px 30px 2px 0",
              }}
            >
              {quantityOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedQuantity(opt);
                  }}
                  className={`hpcl-kg-btn${selectedQuantity === opt ? ' hpcl-kg-btn--active' : ''}`}
                  disabled={cartLoading}
                >
                  {opt}kg
                </button>
              ))}
            </div>
          )}

          {/* Price + Add to Cart */}
          <div
            className="hpcl-footer"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "14px",
              paddingTop: "14px",
            }}
          >
            {!isUser && adjustedCost && (
              <p
                className="hpcl-price"
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                {getCurrencySymbol()}{adjustedCost}{" "}
                {(() => {
                  const ps = priceSuffix
                    ? personServedMap[baseSegment + priceSuffix]
                    : undefined;
                  return ps ? (
                    <span
                      className="hpcl-price-suffix"
                      style={{
                        fontSize: "13px",
                        fontWeight: "normal",
                      }}
                    >
                      ({ps} Person)
                    </span>
                  ) : null;
                })()}
              </p>
            )}
            {showCartOptions && (
              <button
                onClick={addToCart}
                disabled={isInCart || isInEnquiry || cartLoading}
                style={{
                  fontSize: "14px",
                  background: isInCart || isInEnquiry ? "#ccc" : "#fa4848",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: isInCart || isInEnquiry || cartLoading ? "not-allowed" : "pointer",
                  fontWeight: "600",
                  transition: "background 0.2s",
                  minWidth: "100px",
                  marginRight: "30px",
                }}
              >
                {cartLoading
                  ? "Adding..."
                  : isInCart || isInEnquiry
                  ? "In Enquiry"
                  : "Add to Enquiry"}
              </button>
            )}
          </div>
        </div>
      </div>
      {showOngoingModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="ongoing-modal-card"
            style={{
              borderRadius: "10px",
              width: "280px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "#fa4848",
                color: "#fff",
                padding: "10px 14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              <span>On-going Enquiry not Finished</span>
            </div>
            <div className="ongoing-modal-body" style={{ padding: "14px", fontSize: "13px" }}>
              <p style={{ margin: 0 }}>
                You have an active enquiry in progress. Please wait for it to be
                completed before adding a new request.
              </p>
            </div>
            <div
              style={{
                padding: "8px 14px 14px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowOngoingModal(false)}
                style={{
                  background: "#fa4848",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  padding: "6px 16px",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(HomeProfileCardLarge);
